import React, { useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BookingConfirmation = ({ appointment, psychologist, onClose }) => {

  const appointmentDate = appointment?.appointment_date 
    ? format(new Date(appointment.appointment_date + 'T00:00:00'), "d 'de' MMMM 'de' yyyy", { locale: ptBR })
    : '';
    
  // 🔥 FIX: Handle both data structures
  // - Temp public patients: appointment.full_name, appointment.whatsapp_number (direct)
  // - Regular appointments: appointment.patient.full_name, appointment.patient.whatsapp_number (nested)
  const patientName = appointment?.patient?.full_name || appointment?.full_name;
  const patientWhatsapp = appointment?.patient?.whatsapp_number || appointment?.patient?.phone || appointment?.whatsapp_number;

  useEffect(() => {
  // Rola para o topo assim que o componente é montado
  window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 flex items-start justify-center p-4 pt-8 md:p-6 md:pt-12">
      <div className="max-w-2xl w-full bg-card rounded-2xl shadow-2xl border border-border overflow-hidden">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-success to-green-600 p-8 text-white text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={48} color="#FFFFFF" />
          </div>
          <h2 className="text-3xl font-bold mb-2">Agendamento Confirmado!</h2>
          <p className="text-white/90 text-lg">
            Sua consulta foi marcada com sucesso
          </p>
        </div>

        {/* Appointment Details */}
        <div className="p-8">
          <div className="space-y-6">
            {/* Psychologist Info */}
            <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
              {psychologist?.profile_photo_url || psychologist?.clinic_logo_url ? (
                <div className="w-16 h-16 rounded-full overflow-hidden bg-primary/10 flex-shrink-0">
                  <img 
                    src={psychologist?.profile_photo_url || psychologist?.clinic_logo_url}
                    alt={psychologist?.full_name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="User" size={32} color="var(--color-primary)" />
                </div>
              )}
              <div>
                <h3 className="font-semibold text-foreground text-lg">
                  {psychologist?.full_name}
                </h3>
                {psychologist?.specialization && (
                  <p className="text-muted-foreground text-sm">
                    {psychologist?.specialization}
                  </p>
                )}
              </div>
            </div>

            {/* Patient Info */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Icon name="User" size={20} color="var(--color-primary)" className="mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Paciente</p>
                  <p className="font-medium text-foreground">
                    {patientName || ""}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Icon name="Phone" size={20} color="var(--color-primary)" className="mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">WhatsApp</p>
                  <p className="font-medium text-foreground">
                    {patientWhatsapp || ""}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Icon name="Calendar" size={20} color="var(--color-primary)" className="mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Data</p>
                  <p className="font-medium text-foreground capitalize">
                    {appointmentDate}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Icon name="Clock" size={20} color="var(--color-primary)" className="mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Horário (de Brasília, GMT-3)</p>
                  <p className="font-medium text-foreground">
                    {appointment?.start_time}
                  </p>
                </div>
              </div>
            </div>

            {/* WhatsApp Confirmation Notice */}
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Icon name="MessageCircle" size={20} color="var(--color-primary)" className="mt-0.5" />
                <div>
                  <p className="font-medium text-foreground mb-1">
                    Confirmação enviada por WhatsApp
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Você receberá uma mensagem de confirmação com todos os detalhes da consulta no número cadastrado.
                  </p>
                </div>
              </div>
            </div>

            {/* Important Notes */}
            <div className="bg-accent/10 border border-accent/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} color="var(--color-accent)" className="mt-0.5" />
                <div>
                  <p className="font-medium text-foreground mb-2">
                    Informações importantes
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Você receberá um lembrete no dia, pra confirmar a sessão</li>
                    <li>• Esteja pronto 10 minutos antes</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Close Button */}
          <div className="mt-8">
            <Button
              onClick={() => {
                window.location.href = `${import.meta.env.VITE_APP_URL}/public-patient-booking-screen/${psychologist?.id}`;
              }}
              variant="primary"
              size="lg"
              iconName="ArrowLeft"
              iconPosition="left"
              className="w-full"
            >
              Voltar para página inicial
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;